if (!customElements.get('lookbook-card-slider')) {
  customElements.define(
    'lookbook-card-slider',
    class LookbookCardSlider extends HTMLElement {
      #abortController = null;

      constructor() {
        super();
      }

      connectedCallback() {
        this.sliderWrapper = this.querySelector('.swiper');
        if (!this.sliderWrapper) {
          return;
        }

        this.#abortController?.abort();
        this.#abortController = new AbortController();
        const { signal } = this.#abortController;

        this.lookbookIcons = this.querySelectorAll('lookbook-icon');
        this.productCards = this.querySelectorAll('.product-card');
        this.sliderInstance = false;
        this.initSlider();
        this.#setupEventListeners(signal);
      }

      disconnectedCallback() {
        this.#abortController?.abort();
        this.#abortController = null;
      }

      #setupEventListeners(signal) {
        const onMouseOver = this.onMouseOver.bind(this);
        const onMouseLeave = this.onMouseLeave.bind(this);
        const onIconActivate = this.onIconActivate.bind(this);
        const onResize = DBAgency.utils.debounce(() => {
          this.calcNavButtonsPosition();
        }, 100);

        Array.from(this.lookbookIcons).forEach((icon) => {
          icon.addEventListener('mouseover', onMouseOver, { signal });
          icon.addEventListener('mouseleave', onMouseLeave, { signal });
        });

        this.addEventListener('lookbook-icon:activate', onIconActivate, { signal });
        window.addEventListener('resize', onResize, { signal });
      }

      initSlider() {
        const items = parseInt(this.dataset.items);
        const columnGap = DBAgency.utils.getGridColumnGap(this.sliderWrapper);

        this.sliderOptions = {
          init: false,
          slidesPerView: 1,
          spaceBetween: columnGap.desktop,
          breakpoints: {
            768: {
              slidesPerView: items,
            },
          },
          navigation: {
            nextEl: this.querySelector('.swiper-button-next'),
            prevEl: this.querySelector('.swiper-button-prev'),
          },
          pagination: {
            el: this.querySelector('.swiper-pagination'),
            clickable: true,
          },
          speed: 500,
          loop: true,
          threshold: 2,
          on: {
            afterInit: (swiper) => {
              this.waitForSwiperSlides(swiper).then(() => {
                this.onSlideAfterInit(swiper);
              });
            },
            realIndexChange: (swiper) => {
              this.waitForSwiperSlides(swiper).then(() => {
                this.onSlideChange(swiper);
              });
            },
            // Swiper 8 recree ses clones a chaque seuil de largeur, puis emet `breakpoint`.
            breakpoint: () => this.fixQuickviewDuplicate(),
          },
        };

        this.sliderCounter = this.querySelector('.swiper-pagination-counter');
        this.sliderWrapper.querySelector('.swiper-wrapper').classList.remove('f-grid');

        this.sliderInstance = new window.DBAgency.Carousel(this.sliderWrapper, this.sliderOptions);
        this.sliderInstance.init();
        this.sliderInstance.slider.init();

        // Do not use on event like this when set init: false, use on: {} instead
        // this.sliderInstance.slider.on('afterInit', this.onSlideAfterInit.bind(this));
        // this.sliderInstance.slider.on('realIndexChange', this.onSlideChange.bind(this));

        this.fixQuickviewDuplicate();
        this.calcNavButtonsPosition();
      }

      // Helper function to wait for swiper.slides to be available
      waitForSwiperSlides(swiper, maxAttempts = 50, interval = 100) {
        return new Promise((resolve, reject) => {
          let attempts = 0;

          const checkSlides = () => {
            if (swiper.slides && swiper.slides.length > 0) {
              resolve(swiper);
            } else if (attempts >= maxAttempts) {
              reject(new Error('Swiper slides not available after maximum attempts'));
            } else {
              attempts++;
              setTimeout(checkSlides, interval);
            }
          };

          checkSlides();
        });
      }

      calcNavButtonsPosition() {
        if (this.dataset.calcButtonPosition !== 'true') return;

        const firstMedia = this.querySelector('.product-card__image-wrapper');
        if (firstMedia && firstMedia.clientHeight > 0) {
          this.style.setProperty('--swiper-navigation-top-offset', parseInt(firstMedia.clientHeight) / 2 + 'px');
        }
      }

      // Une seule fenetre d'ajout rapide par produit, et celle de la carte REELLE. Les clones de
      // Swiper 8 la recopient, identifiant compris, et sont recrees a chaque seuil de largeur.
      // Garder la premiere venue gardait tantot celle d'un clone (supprimee avec lui : bouton
      // mort), tantot laissait celles des nouveaux clones (deux fenetres d'un clic) — 16/10/2026.
      fixQuickviewDuplicate() {
        const groups = new Map();
        Array.from(this.querySelectorAll('quick-view-modal')).forEach((modal) => {
          if (!groups.has(modal.id)) groups.set(modal.id, []);
          groups.get(modal.id).push(modal);
        });
        groups.forEach((modals) => {
          if (modals.length < 2) return;
          const keep = modals.find((modal) => !modal.closest('.swiper-slide-duplicate')) || modals[0];
          modals.forEach((modal) => {
            if (modal !== keep) modal.remove();
          });
          // Une fenetre ne se branche que sur les boutons presents a son insertion : la
          // reinserer la branche aussi sur ceux des clones.
          keep.parentNode.insertBefore(keep, keep.nextSibling);
        });
      }

      onSlideAfterInit(swiper) {
        const { realIndex } = swiper;
        if (this.sliderCounter && swiper.pagination.bullets) {
          this.sliderCounter.querySelector('.swiper-pagination-current').innerText = realIndex + 1;
          this.sliderCounter.querySelector('.swiper-pagination-total').innerText = swiper.pagination.bullets.length;
        }

        this.detectVisibleSlides(swiper);
      }

      onSlideChange(swiper) {
        const { slides, realIndex } = swiper;

        this.detectVisibleSlides(swiper);

        if (slides[realIndex]) {
          Array.from(this.lookbookIcons).forEach((icon) => icon.classList.remove('is-active'));
          this.lookbookIcons[realIndex] && this.lookbookIcons[realIndex].classList.add('is-active');
        }

        if (this.sliderCounter && swiper.pagination.bullets) {
          this.sliderCounter.querySelector('.swiper-pagination-current').innerText = realIndex + 1;
        }
      }

      detectVisibleSlides(swiper) {
        // Check if swiper.slides is available before proceeding
        if (!swiper.slides || swiper.slides.length === 0) {
          console.warn('Swiper slides not available yet');
          return;
        }

        const {
          activeIndex,
          params: { slidesPerView },
        } = swiper;

        this.activeIndexs = [];
        for (let i = 0; i < slidesPerView; i++) {
          const slideIndex = (activeIndex + i) % swiper.slides.length;
          const index = Number(swiper.slides[slideIndex].dataset.index);
          this.activeIndexs.push(index);
        }

        this.animateVisiblePins();
      }

      animateVisiblePins() {
        this.resetAnimatePins();
        this.startAnimatePins();
      }

      resetAnimatePins() {
        Array.from(this.lookbookIcons).forEach((icon) => icon.classList.remove('is-visible'));
      }

      startAnimatePins() {
        Array.from(this.activeIndexs).forEach((index) => {
          this.lookbookIcons[index] && this.lookbookIcons[index].classList.add('is-visible');
        });
      }

      onIconActivate(event) {
        const { index } = event.detail;

        if (index === null || Number.isNaN(index)) {
          return;
        }

        const target = this.lookbookIcons[index];
        if (target) {
          this.activateIcon(target);
        }
      }

      // Sur telephone les deux colonnes sont empilees : la fiche selectionnee est sous la
      // grande image, hors ecran. Sans ca, selectionner un point ne fait rien de visible.
      //
      // Appelee depuis activateIcon() et non depuis onIconActivate() : lookbook-icon.js ne
      // branche le clic QUE sur un appareil sans souris. Dans l'apercu de l'editeur ou dans
      // un navigateur de bureau retreci, la mise en page est celle du telephone mais le
      // pointeur reste fin — le clic n'existe pas, seul le survol passe. En se branchant sur
      // activateIcon() on couvre les deux chemins. Le garde mqlMobile suffit a ne rien faire
      // des que les colonnes reviennent cote a cote.
      scrollToProducts() {
        if (!DBAgency.config.mqlMobile) return;

        const rect = this.sliderWrapper.getBoundingClientRect();
        const headerHeight =
          parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;
        const available = window.innerHeight - headerHeight;

        // Fiche plus haute que l'ecran : on cale son haut sous l'en-tete plutot que de la
        // centrer, ce qui en couperait l'image par le haut.
        const offset = headerHeight + Math.max(0, (available - rect.height) / 2);

        window.scrollTo({
          top: rect.top + window.scrollY - offset,
          behavior: DBAgency.config.motionReduced || DBAgency.config.disableAnimations ? 'auto' : 'smooth',
        });
      }

      activateIcon(target) {
        const index = Number(target.dataset.index);
        target.classList.add('is-active');

        if (typeof this.sliderInstance.slider === 'object') {
          if (this.sliderOptions.loop) {
            this.sliderInstance.slider.slideToLoop(index, this.sliderOptions.speed);
          } else {
            this.sliderInstance.slider.slideTo(index, this.sliderOptions.speed);
          }

          this.classList.add('is-hovering');
          this.resetAnimatePins();
        }

        this.scrollToProducts();
      }

      onMouseOver(event) {
        this.activateIcon(event.currentTarget);
      }

      resetActiveIcons() {
        Array.from(this.productCards).forEach((card) => card.classList.remove('is-active'));
        Array.from(this.lookbookIcons).forEach((icon) => icon.classList.remove('is-active'));
        this.classList.remove('is-hovering');
        this.startAnimatePins();
      }

      onMouseLeave() {
        this.resetActiveIcons();
      }
    }
  );
}